// ...existing code...

// ...existing code...
import { AxiosResponse } from 'axios';
import { axiosInstance } from './apiService';
import {
  Budget,
  BudgetCreate,
  BudgetVersion,
  BudgetVersionCreate,
  BudgetVersionFullSave,
  BudgetVersionEstado,
  BudgetVersionTransitionRequest,
  BudgetVersionTransitionResult,
  Environment,
  EnvironmentCreate,
  EnvironmentMaterial,
  EnvironmentMaterialCreate,
  EnvironmentInstallation,
  EnvironmentInstallationCreate,
  EnvironmentInstallationMaterial,
  EnvironmentInstallationMaterialCreate,
  Freight,
  FreightCreate,
  BudgetFilters,
  PaginatedResponse,
  VersionSectionSummary,
  CountryConfig,
  CountryConfigUpdate,
  Comment,
  CommentCreate,
  CommentUpdate,
  PorcentajeHUpdateResult
} from '../types';

export class BudgetApiService {
  // ========= BUDGET ENDPOINTS =========
  
  // Listar presupuestos del usuario
  static async listBudgets(filters: BudgetFilters = {}): Promise<PaginatedResponse<Budget>> {
    const response: AxiosResponse<PaginatedResponse<Budget>> = await axiosInstance.get('/budgets', {
      params: filters
    });
    return response.data;
  }

  // Obtener presupuesto por ID
  static async getBudget(budgetId: number): Promise<Budget> {
    const response: AxiosResponse<Budget> = await axiosInstance.get(`/budgets/${budgetId}`);
    return response.data;
  }

  // Crear nuevo presupuesto
  static async createBudget(budgetData: BudgetCreate): Promise<Budget> {
    const response: AxiosResponse<Budget> = await axiosInstance.post('/budgets', budgetData);
    return response.data;
  }

  // Actualizar presupuesto
  static async updateBudget(budgetId: number, budgetData: Partial<BudgetCreate>): Promise<Budget> {
    const response: AxiosResponse<Budget> = await axiosInstance.put(`/budgets/${budgetId}`, budgetData);
    return response.data;
  }


  // Eliminar presupuesto
  static async deleteBudget(budgetId: number): Promise<void> {
    await axiosInstance.delete(`/budgets/${budgetId}`);
  }

  // Cambiar el estado del presupuesto
  static async updateBudgetStatus(budgetId: number, status: string): Promise<Budget> {
    const response: AxiosResponse<Budget> = await axiosInstance.put(`/budgets/${budgetId}/status`, { status });
    return response.data;
  }

  // Duplicar presupuesto
  static async duplicateBudget(budgetId: number): Promise<Budget> {
    const response: AxiosResponse<Budget> = await axiosInstance.post(`/budgets/${budgetId}/duplicate`);
    return response.data;
  }

  // Exportar PDF
  // PDF generation runs as a background RQ job (CLAUDE.md §3), not inline in
  // the request - these three calls replace the old single synchronous
  // exportPdf(): enqueue, poll status, download once finished. This is what
  // keeps a slow render from ever holding one HTTP connection open long
  // enough for nginx's proxy_read_timeout to sever it mid-PDF.

  static async startPdfExport(budgetId: number, versionId?: number): Promise<{ job_id: string; status: string }> {
    const url = versionId
      ? `/budgets/${budgetId}/export/pdf?version_id=${versionId}`
      : `/budgets/${budgetId}/export/pdf`;
    const response: AxiosResponse<{ job_id: string; status: string }> = await axiosInstance.post(url);
    return response.data;
  }

  static async getPdfExportStatus(budgetId: number, jobId: string): Promise<{ job_id: string; status: string; error?: string }> {
    const response: AxiosResponse<{ job_id: string; status: string; error?: string }> = await axiosInstance.get(
      `/budgets/${budgetId}/export/pdf/status/${jobId}`
    );
    return response.data;
  }

  static async downloadPdfExport(budgetId: number, jobId: string): Promise<Blob> {
    const response: AxiosResponse<Blob> = await axiosInstance.get(
      `/budgets/${budgetId}/export/pdf/download/${jobId}`,
      { responseType: 'blob' }
    );
    return response.data;
  }

  // ========= BUDGET VERSION ENDPOINTS =========

  // Crear nueva versión de presupuesto
  static async createBudgetVersion(budgetId: number, versionData: BudgetVersionCreate): Promise<BudgetVersion> {
    const response: AxiosResponse<BudgetVersion> = await axiosInstance.post(`/budgets/${budgetId}/versions`, versionData);
    return response.data;
  }

  // Actualizar versión de presupuesto
  static async updateBudgetVersion(budgetId: number, versionId: number, versionData: Partial<BudgetVersionCreate>): Promise<BudgetVersion> {
    const response: AxiosResponse<BudgetVersion> = await axiosInstance.put(`/budgets/${budgetId}/versions/${versionId}`, versionData);
    return response.data;
  }

  // Guardar TODO el estado de una versión en una sola llamada transaccional
  // (ambientes + materiales + instalaciones + materiales de instalación +
  // fletes) - reemplaza el guardado anterior de un request HTTP por ítem.
  // Ver CLAUDE.md §10 field reports #2 y #7.
  static async saveFullVersion(budgetId: number, versionId: number, data: BudgetVersionFullSave): Promise<BudgetVersion> {
    const response: AxiosResponse<BudgetVersion> = await axiosInstance.put(
      `/budgets/${budgetId}/versions/${versionId}/full`, data
    );
    return response.data;
  }

  // Obtener versión con detalles completos
  static async getBudgetVersion(budgetId: number, versionId: number): Promise<BudgetVersion> {
    const response: AxiosResponse<BudgetVersion> = await axiosInstance.get(`/budgets/${budgetId}/versions/${versionId}`);
    return response.data;
  }

  // Obtener todas las versiones de un presupuesto
  static async getBudgetVersions(budgetId: number): Promise<BudgetVersion[]> {
    const response: AxiosResponse<BudgetVersion[]> = await axiosInstance.get(`/budgets/${budgetId}/versions`);
    return response.data;
  }

  // Cambiar el estado de una versión corriendo las validaciones que ese
  // cambio exija (CLAUDE.md §5): chequeo de precios desactualizados en
  // Materiales, confirmación/reconfirmación de la fecha prevista de entrega, y
  // lock de la cotización oficial. Es el único camino hacia 'sent' y 'sold' -
  // updateBudgetVersion los rechaza a propósito.
  //
  // Errores que el llamador tiene que distinguir:
  //   422 + detail.code === 'delivery_date_required' -> pedir la fecha
  //        (detail.suggested trae con qué precargar el prompt) y reintentar
  //   502 + detail.code === 'fx_unavailable' -> el banco central no respondió;
  //        no se envió nada, reintentar es lo correcto
  static async transitionVersion(
    budgetId: number,
    versionId: number,
    targetEstado: BudgetVersionEstado,
    fechaPrevistaEntrega?: string,
    reason?: string
  ): Promise<BudgetVersionTransitionResult> {
    const body: BudgetVersionTransitionRequest = { target_estado: targetEstado };
    if (fechaPrevistaEntrega) body.fecha_prevista_entrega = fechaPrevistaEntrega;
    if (reason) body.reason = reason;
    const response: AxiosResponse<BudgetVersionTransitionResult> = await axiosInstance.post(
      `/budgets/${budgetId}/versions/${versionId}/transition`, body
    );
    return response.data;
  }

  // Clonar versión
  static async cloneBudgetVersion(budgetId: number, versionId: number, titulo?: string): Promise<BudgetVersion> {
    const body: any = { source_version_id: versionId };
    if (titulo) body.titulo = titulo;
    const response: AxiosResponse<BudgetVersion> = await axiosInstance.post(`/budgets/${budgetId}/versions/${versionId}/clone`, body);
    return response.data;
  }

  // Resumen por sección (Materiales USD+IVA, Mano de Obra convertida a
  // moneda local con la cotización YA congelada, Flete neto de Bonificado) -
  // CLAUDE.md §6/§10 field report #6. Única fuente de esta cuenta: leer esto
  // en vez de recalcularlo por su cuenta (eso fue exactamente lo que hizo
  // que "Calculadora" mostrara un split viejo).
  static async getVersionSummary(budgetId: number, versionId: number): Promise<VersionSectionSummary> {
    const response: AxiosResponse<VersionSectionSummary> = await axiosInstance.get(
      `/budgets/${budgetId}/versions/${versionId}/summary`
    );
    return response.data;
  }

  // Calculadora H: guardar % H. Si H supera el umbral (Facturado < 50%), la
  // versión pasa a 'pending_approval' - la MISMA cola de aprobación que el
  // chequeo de precios de Materiales, no un mecanismo aparte. Un 200 no dice
  // por sí solo si eso pasó: revisar `diverted_to_approval` en la respuesta.
  // allowSold: editar el % H (valor de split para la factura ERP, §8) sobre una
  // versión ya 'sold' - salta la puerta de aprobación y no cambia el estado.
  // Sólo lo usa el popup de fecha-de-entrega en enviado->sold (CLAUDE.md §5).
  static async updatePorcentajeH(
    budgetId: number,
    versionId: number,
    porcentajeH: number,
    allowSold: boolean = false
  ): Promise<PorcentajeHUpdateResult> {
    const response: AxiosResponse<PorcentajeHUpdateResult> = await axiosInstance.put(
      `/budgets/${budgetId}/versions/${versionId}/h-percentage`,
      { porcentaje_h: porcentajeH, allow_sold: allowSold }
    );
    return response.data;
  }

  // ========= CONFIG POR PAÍS (Phase 6, CLAUDE.md §6) =========
  // IVA % y si la sección Flete está habilitada, por país activo.

  static async getCountryConfig(): Promise<CountryConfig> {
    const response: AxiosResponse<CountryConfig> = await axiosInstance.get('/settings/config/country');
    return response.data;
  }

  // Admin-only en el backend (403 para no-admins).
  static async updateCountryConfig(data: CountryConfigUpdate): Promise<CountryConfig> {
    const response: AxiosResponse<CountryConfig> = await axiosInstance.put('/settings/config/country', data);
    return response.data;
  }

  // ========= COMMENTS (CLAUDE.md §10 field report #11) =========
  // Crear/editar/eliminar solo funciona mientras la versión sea
  // content-editable (draft/pending_approval/approved) - el backend devuelve
  // 400 una vez enviada, sin excepción para los internos. Listar siempre
  // funciona, en cualquier estado.

  static async listComments(budgetId: number, versionId: number): Promise<Comment[]> {
    const response: AxiosResponse<Comment[]> = await axiosInstance.get(
      `/budgets/${budgetId}/versions/${versionId}/comments`
    );
    return response.data;
  }

  static async createComment(budgetId: number, versionId: number, data: CommentCreate): Promise<Comment> {
    const response: AxiosResponse<Comment> = await axiosInstance.post(
      `/budgets/${budgetId}/versions/${versionId}/comments`, data
    );
    return response.data;
  }

  static async updateComment(commentId: number, data: CommentUpdate): Promise<Comment> {
    const response: AxiosResponse<Comment> = await axiosInstance.put(`/budgets/comments/${commentId}`, data);
    return response.data;
  }

  static async deleteComment(commentId: number): Promise<void> {
    await axiosInstance.delete(`/budgets/comments/${commentId}`);
  }

  // Calcular totales de la versión
  static async calculateVersionTotals(budgetId: number, versionId: number): Promise<BudgetVersion> {
    const response: AxiosResponse<BudgetVersion> = await axiosInstance.post(`/budgets/${budgetId}/versions/${versionId}/calculate-totals`);
    return response.data;
  }

  // ========= ENVIRONMENT ENDPOINTS =========

  // Crear ambiente en una versión
  static async createEnvironment(budgetId: number, versionId: number, environmentData: EnvironmentCreate): Promise<Environment> {
    const response: AxiosResponse<Environment> = await axiosInstance.post(`/budgets/${budgetId}/versions/${versionId}/environments`, environmentData);
    return response.data;
  }

  // Obtener ambiente por ID
  static async getEnvironment(environmentId: number): Promise<Environment> {
    const response: AxiosResponse<Environment> = await axiosInstance.get(`/budgets/environments/${environmentId}`);
    return response.data;
  }

  // Actualizar ambiente
  static async updateEnvironment(environmentId: number, environmentData: Partial<EnvironmentCreate>): Promise<Environment> {
    const response: AxiosResponse<Environment> = await axiosInstance.put(`/budgets/environments/${environmentId}`, environmentData);
    return response.data;
  }

  // Eliminar ambiente
  static async deleteEnvironment(environmentId: number): Promise<void> {
    await axiosInstance.delete(`/budgets/environments/${environmentId}`);
  }

  // ========= ENVIRONMENT MATERIAL ENDPOINTS =========

  // Crear material de ambiente (genérico)
  static async createEnvironmentMaterial(environmentId: number, materialData: EnvironmentMaterialCreate): Promise<EnvironmentMaterial> {
    const response: AxiosResponse<EnvironmentMaterial> = await axiosInstance.post(`/budgets/environments/${environmentId}/materials`, materialData);
    return response.data;
  }

  // Crear material desde producto del catálogo
  static async createEnvironmentMaterialFromProduct(environmentId: number, productId: number, materialData: EnvironmentMaterialCreate): Promise<EnvironmentMaterial> {
    const response: AxiosResponse<EnvironmentMaterial> = await axiosInstance.post(`/budgets/environments/${environmentId}/materials/from-product/${productId}`, materialData);
    return response.data;
  }

  // Actualizar material de ambiente
  static async updateEnvironmentMaterial(materialId: number, materialData: Partial<EnvironmentMaterialCreate>): Promise<EnvironmentMaterial> {
    const response: AxiosResponse<EnvironmentMaterial> = await axiosInstance.put(`/budgets/materials/${materialId}`, materialData);
    return response.data;
  }

  // Eliminar material de ambiente
  static async deleteEnvironmentMaterial(materialId: number): Promise<void> {
    await axiosInstance.delete(`/budgets/materials/${materialId}`);
  }

  // ========= FREIGHT ENDPOINTS =========

  // Crear flete
  static async createFreight(budgetId: number, versionId: number, freightData: FreightCreate): Promise<Freight> {
    const response: AxiosResponse<Freight> = await axiosInstance.post(`/budgets/${budgetId}/versions/${versionId}/freights`, freightData);
    return response.data;
  }

  // Actualizar flete
  static async updateFreight(freightId: number, freightData: Partial<FreightCreate>): Promise<Freight> {
    const response: AxiosResponse<Freight> = await axiosInstance.put(`/budgets/freights/${freightId}`, freightData);
    return response.data;
  }

  // Eliminar flete
  static async deleteFreight(freightId: number): Promise<void> {
    await axiosInstance.delete(`/budgets/freights/${freightId}`);
  }

  // ========= ENVIRONMENT INSTALLATION ENDPOINTS =========

  // Crear instalación de ambiente
  static async createEnvironmentInstallation(environmentId: number, installationData: EnvironmentInstallationCreate): Promise<EnvironmentInstallation> {
    const response: AxiosResponse<EnvironmentInstallation> = await axiosInstance.post(`/budgets/environments/${environmentId}/installation`, installationData);
    return response.data;
  }

  // Actualizar instalación de ambiente
  static async updateEnvironmentInstallation(installationId: number, installationData: Partial<EnvironmentInstallationCreate>): Promise<EnvironmentInstallation> {
    const response: AxiosResponse<EnvironmentInstallation> = await axiosInstance.put(`/budgets/installations/${installationId}`, installationData);
    return response.data;
  }

  // Eliminar instalación de ambiente
  static async deleteEnvironmentInstallation(installationId: number): Promise<void> {
    await axiosInstance.delete(`/budgets/installations/${installationId}`);
  }

  // ========= INSTALLATION MATERIAL ENDPOINTS =========

  // Crear material de instalación
  static async createInstallationMaterial(installationId: number, materialData: EnvironmentInstallationMaterialCreate): Promise<EnvironmentInstallationMaterial> {
    const response: AxiosResponse<EnvironmentInstallationMaterial> = await axiosInstance.post(`/budgets/installations/${installationId}/materials`, materialData);
    return response.data;
  }

  // Actualizar material de instalación
  static async updateInstallationMaterial(materialId: number, materialData: Partial<EnvironmentInstallationMaterialCreate>): Promise<EnvironmentInstallationMaterial> {
    const response: AxiosResponse<EnvironmentInstallationMaterial> = await axiosInstance.put(`/budgets/installation-materials/${materialId}`, materialData);
    return response.data;
  }

  // Eliminar material de instalación
  static async deleteInstallationMaterial(materialId: number): Promise<void> {
    await axiosInstance.delete(`/budgets/installation-materials/${materialId}`);
  }

  // ========= UTILITY FUNCTIONS =========

  // Obtener total de presupuesto
  static calculateBudgetTotal(version: BudgetVersion): number {
    // Los totales ya incluyen descuentos por ambiente
    const materialTotal = version.total_materiales || 0;
    const freightTotal = version.total_flete || 0;
    const installationTotal = version.total_instalacion || 0;
    
    return materialTotal + freightTotal + installationTotal;
  }

  // Formatear moneda
  static formatCurrency(amount: number, currency: string = 'USD'): string {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: currency === 'USD' ? 'USD' : 'ARS'
    }).format(amount);
  }

  // Obtener color del status
  static getStatusColor(status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' {
    switch (status) {
      case 'draft': return 'default';
      case 'pending_approval': return 'warning';
      case 'approved': return 'info';
      case 'rejected': return 'error';
      case 'sent': return 'primary';
      case 'sold': return 'success';
      case 'lost': return 'error';
      default: return 'default';
    }
  }

  // Obtener label del status
  static getStatusLabel(status: string): string {
    switch (status) {
      case 'draft': return 'Borrador';
      case 'pending_approval': return 'Pendiente de aprobación';
      case 'approved': return 'Aprobado';
      case 'rejected': return 'No Aprobado';
      // "Enviar": el PO primero eligió "Enviado" y luego lo revirtió a
      // "Enviar" el 2026-09-06 (CLAUDE.md §5). Cambio de label solamente -
      // el valor del enum sigue siendo 'sent'. Espeja STATE_LABELS del backend.
      case 'sent': return 'Enviar';
      case 'sold': return 'Vendido';
      case 'lost': return 'Perdido';
      default: return status;
    }
  }

  // Estadísticas del Dashboard
  static async getStats(): Promise<any> {
    const response = await axiosInstance.get('/dashboard/stats');
    return response.data;
  }

  static async getAdvancedStats(): Promise<any> {
    const response = await axiosInstance.get('/dashboard/advanced-stats');
    return response.data;
  }

  static async getUserActivityStats(limit: number = 10): Promise<any> {
    const response = await axiosInstance.get('/dashboard/user-activity-stats', {
      params: { limit }
    });
    return response.data;
  }

  static async getUserSalesStats(limit: number = 10): Promise<any> {
    const response = await axiosInstance.get('/dashboard/user-sales-stats', {
      params: { limit }
    });
    return response.data;
  }

  // Estadísticas de Productos (endpoints directos)
  static async getProductsMostUsed(limit: number = 10): Promise<any> {
    const response = await axiosInstance.get('/products/stats/most-used', {
      params: { limit }
    });
    return response.data;
  }

  static async getProductsWithoutStock(limit: number = 10): Promise<any> {
    const response = await axiosInstance.get('/products/stats/without-stock', {
      params: { limit }
    });
    return response.data;
  }

  static async getProductsInactive(limit: number = 10): Promise<any> {
    const response = await axiosInstance.get('/products/stats/inactive', {
      params: { limit }
    });
    return response.data;
  }

  static async getProductsOutlet(limit: number = 10): Promise<any> {
    const response = await axiosInstance.get('/products/stats/outlet', {
      params: { limit }
    });
    return response.data;
  }
}

export default BudgetApiService;